import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import toast from "react-hot-toast";
import { CompanyService } from "services/companyService";
import { JournalVoucher4Service } from "services/journal-voucher-4";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

const initialValues = {
  unit: "",
  address: "",
  month: String(today.getMonth() + 1),
  year: String(today.getFullYear()),
  debitAccountLabels: Array(10).fill(""),
  rows: [],
  totals: {
    creditValues: Array(10).fill(0),
    totalCredit341: 0,
  },
  recordedToLedgerDay: "",
  recordedToLedgerMonth: "",
  recordedToLedgerYear: "",
  reportDate: "",
  ledgerClerk: "",
  chiefAccountant: "",
  legalRepresentative: "",
  signingDay: String(today.getDate()),
  signingMonth: String(today.getMonth() + 1),
  signingYear: String(today.getFullYear()),
};

function JournalVoucher4Detail() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(initialValues);

  const queryParams = new URLSearchParams(location.search);
  const filters = {
    year: queryParams.get("year") || String(today.getFullYear()),
    month: queryParams.get("month") || String(today.getMonth() + 1),
    accountCode: queryParams.get("accountCode") || "",
    searchBy: queryParams.get("searchBy") || "",
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const companyResp = await CompanyService.getByAuth();
        const companyData = companyResp?.data || companyResp || {};
        const response = await JournalVoucher4Service.preview({
          company: companyData?._id || "me",
          year: filters.year,
          month: filters.month,
          accountCode: filters.accountCode,
        });
        const payload = response?.data || response || {};
        const normalizedSearch = filters.searchBy.trim().toLowerCase();
        const filteredRows = normalizedSearch
          ? (payload.rows || []).filter((item) => {
              const counterpartText = (payload.debitAccountLabels || [])
                .filter((label, index) => Number(item?.debitValues?.[index] || 0) > 0)
                .join(" ");

              return [item.voucherNumber, item.voucherDate, item.description, counterpartText]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(normalizedSearch);
            })
          : payload.rows || [];

        const debitValues = Array.from({ length: 10 }, (_, index) =>
          filteredRows.reduce((total, row) => total + Number(row?.debitValues?.[index] || 0), 0)
        );

        setData({
          unit: companyData.name || "",
          address: companyData.address || "",
          month: filters.month,
          year: filters.year,
          accountCode: filters.accountCode,
          debitAccountLabels: payload.debitAccountLabels || Array(10).fill(""),
          rows: filteredRows,
          totals: {
            debitValues,
            totalCredit341: filteredRows.reduce((total, row) => total + Number(row.totalCredit341 || 0), 0),
          },
          recordedToLedgerDay: "",
          recordedToLedgerMonth: "",
          recordedToLedgerYear: "",
          reportDate: `${String(today.getDate()).padStart(2, "0")}/${String(today.getMonth() + 1).padStart(2, "0")}/${today.getFullYear()}`,
          ledgerClerk: "",
          chiefAccountant: "",
          legalRepresentative: "",
          signingDay: String(today.getDate()),
          signingMonth: String(today.getMonth() + 1),
          signingYear: String(today.getFullYear()),
        });
      } catch (error) {
        toast.error(error?.response?.data?.message || "Không thể tải Nhật ký Chứng từ số 4");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters.month, filters.searchBy, filters.year]);

  const handlePrint = () => {
    const html = getPrintTemplate(data);
    const win = window.open("", "_blank");
    win.document.open();
    win.document.write(`<html><head><title>Print S04a4-DN</title>
      <style>@media print{@page{size:A4 landscape;margin:1cm}}body{margin:0}</style>
      </head><body onload="setTimeout(()=>[window.print(),window.close()], 500)">${html}</body></html>`);
    win.document.close();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox p={3} display="flex" flexDirection="column" minHeight="calc(100vh - 220px)">
                {loading ? (
                  <SoftBox display="flex" justifyContent="center" p={5}>
                    <CircularProgress color="info" />
                  </SoftBox>
                ) : (
                  <>
                    <SoftBox mb={2}>
                      <SoftTypography variant="h5" fontWeight="bold">
                        Nhật ký Chứng từ số 4 (S04a4-DN)
                      </SoftTypography>
                      <SoftTypography variant="body2" color="text">
                        Dữ liệu map tự động cho tháng {filters.month}/{filters.year}
                      </SoftTypography>
                    </SoftBox>

                    <SoftBox border="1px solid #000" p={3} sx={{ fontFamily: "'Times New Roman', serif", color: "#000" }}>
                      <div style={{ fontSize: 14, fontWeight: 700, textAlign: "center", marginBottom: 8 }}>Mẫu số S04a4-DN</div>
                      <div style={{ fontSize: 14, textAlign: "center", marginBottom: 16 }}>
                        (Kèm theo Thông tư số 99/2025/TT-BTC ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 700, textAlign: "center", textTransform: "uppercase" }}>NHẬT KÝ CHỨNG TỪ SỐ 4</div>
                      <div style={{ fontSize: 14, fontStyle: "italic", textAlign: "center", marginTop: 8 }}>
                        Ghi Có Tài khoản 341 - Vay và nợ thuê tài chính
                      </div>
                      <div style={{ fontSize: 14, textAlign: "center", marginTop: 4 }}>
                        Tháng {filters.month} năm {filters.year}
                      </div>
                      <div style={{ marginTop: 16, fontSize: 14 }}>
                        Tổng số dòng: {data.rows.length} | Tổng phát sinh Có TK 341:{" "}
                        {new Intl.NumberFormat("vi-VN").format(data.totals.totalCredit341 || 0)}
                      </div>
                    </SoftBox>

                    <SoftBox mt="auto" pt={2} display="flex" justifyContent="flex-end" gap={1}>
                      <SoftButton variant="outlined" color="dark" onClick={() => navigate("/templates/journal-voucher-4")}>
                        Quay lại
                      </SoftButton>
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                        In Bảng
                      </SoftButton>
                    </SoftBox>
                  </>
                )}
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default JournalVoucher4Detail;
